import {
  ActiveReq,
  LoginPhoneReq,
  LoginReq,
  LoginResp,
  MeResp,
  RefreshResp,
  RegisterReq,
} from "../interfaces/services/Auth";
import apiUrls from "../store/apiUrls";
import apiService, {
  apiCachedGet,
  invalidateCacheKey,
  invalidateCachePrefix,
} from "./apiService";

const CK = {
  userByCode: (userCode: string) => `AUTH:user:${userCode}`,
  userList: (page: number, om?: boolean, s?: string) =>
    `AUTH:userlist:${page}:${om ?? ""}:${s ?? ""}`,
  me: () => `AUTH:me`,
};

const AUTH_NS = "AUTH:";

export const invalidateAuthAll = () => invalidateCachePrefix(AUTH_NS);
const invalidateMe = () => invalidateCacheKey(CK.me());
const invalidateUser = (userCode?: string) =>
  userCode ? invalidateCacheKey(CK.userByCode(userCode)) : undefined;

const authService = {
  login: async (credentials: LoginReq): Promise<LoginResp> => {
    const res = await apiService.AUTH.post(
      apiUrls.auth.sign.signIn,
      credentials,
    );
    invalidateAuthAll();
    return res;
  },
  loginPhone: async (credentials: LoginPhoneReq): Promise<LoginResp> => {
    const res = await apiService.AUTH.post(
      apiUrls.auth.sign.signInPhone,
      credentials,
    );
    invalidateAuthAll();
    return res;
  },
  logOut: async (refreshToken: string): Promise<string> => {
    const res = await apiService.AUTH.post(apiUrls.auth.sign.signOut, {
      refreshToken,
    });
    invalidateAuthAll();
    invalidateMe();
    return res;
  },

  resetPassword: async ({
    email,
    password,
    newPassword,
  }: {
    email: string;
    password: string;
    newPassword: string;
  }): Promise<MeResp> => {
    const res = await apiService.AUTH.post(
      `${apiUrls.auth.sign.resetPassword}`,
      {
        email,
        password,
        newPassword,
      },
    );
    invalidateAuthAll();
    invalidateMe();
    return res;
  },

  updateUserProfile: async ({
    name,
    lastname,
    imageUrl,
  }: {
    name: string;
    lastname: string;
    imageUrl: string;
  }): Promise<MeResp> => {
    const res = await apiService.AUTH.post(`${apiUrls.auth.user.userProfile}`, {
      name,
      lastname,
      imageUrl,
    });
    invalidateAuthAll();
    invalidateMe();
    return res;
  },

  register: async (credentials: RegisterReq) => {
    const res = await apiService.AUTH.put(
      apiUrls.auth.sign.signUp,
      credentials,
    );
    invalidateAuthAll();
    return res;
  },
  forgot: async (email: string) => {
    const res = await apiService.AUTH.put(apiUrls.auth.sign.forgotPassword, {
      email,
    });
    invalidateAuthAll();
    return res;
  },
  forgotReset: async (email: string, code: string, password: string) => {
    const res = await apiService.AUTH.put(
      apiUrls.auth.sign.forgotPasswordReset,
      {
        email,
        code,
        password,
      },
    );
    invalidateAuthAll();
    return res;
  },
  activate: async (credentials: ActiveReq) => {
    const res = await apiService.AUTH.post(
      apiUrls.auth.sign.activate,
      credentials,
    );
    invalidateAuthAll();
    return res;
  },
  me: (): Promise<MeResp> => {
    const url = apiUrls.auth.user.me;
    return apiCachedGet<MeResp>("AUTH", url, {
      keyExtra: CK.me(),
    });
  },
  refreshToken: (refreshToken: string): Promise<RefreshResp> => {
    return apiService.AUTH.post(apiUrls.auth.sign.refreshToken, {
      refreshToken,
    });
  },
  getUserWithCode: (userCode: string): Promise<MeResp> => {
    if (!userCode) return Promise.resolve({} as MeResp);
    const url = `${apiUrls.auth.user.userWithCode}/${userCode}`;
    return apiCachedGet<MeResp>("AUTH", url, {
      ttl: 10 * 60 * 1000, // 10dk
      keyExtra: CK.userByCode(userCode),
    });
  },
};

export default authService;
